import type { Component } from 'vue'
import {
  Minus,
  PenLine,
  Clock,
  Eye,
  CheckCircle,
  ThumbsDown,
  AlertCircle,
  AlertTriangle,
  XCircle,
} from 'lucide-vue-next'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProposalStatusCategory = 'positive' | 'negative' | 'neutral'

export type ProposalStatusKey =
  | 'not_started'
  | 'started_by_vendor'
  | 'active_not_accessed'
  | 'active_awaiting_response'
  | 'completed_accepted'
  | 'completed_rejected'
  | 'completed_expired'
  | 'cancelled'
  | 'stale_reports'

/**
 * All Tailwind color slots consumed by proposal status cards and the
 * pipeline drawer. Every slot is a full, JIT-safe class string (no
 * interpolation) so Tailwind can detect them in content scanning.
 */
export interface ProposalStatusColors {
  /** Card border */
  border: string
  /** Card gradient background */
  cardBg: string
  /** Hover glow shadow colour */
  hoverShadow: string
  /** Hover overlay gradient */
  hoverBg: string
  /** Progress bar track (background) */
  barTrack: string
  /** Progress bar fill */
  barFill: string
  /** Icon container background */
  iconBg: string
  /** Icon colour */
  iconColor: string
  /** Large count number colour */
  valueColor: string
  /** Status label colour */
  labelColor: string
  /** Sub-label / caption colour */
  subColor: string
  /** Horizontal divider line */
  divider: string
  /** Drawer header title colour */
  title: string
  /** Drawer "view proposal" link classes */
  viewLink: string
}

export interface ProposalStatusConfig {
  key: ProposalStatusKey
  category: ProposalStatusCategory
  /** 1 = low intensity, 2 = mid, 3 = high (within each category) */
  intensity: 1 | 2 | 3
  label: string
  sublabel: string
  description: string
  icon: Component
  colors: ProposalStatusColors
  /** Actor badge shown on active-pipeline dashboard cards */
  actor?: 'vendor' | 'customer'
}

// ─── Category palettes (shade-based intensity) ───────────────────────────────
//
// positive: brand green  — intensity goes brand-50 → brand-100 → brand-200 bg
// negative: red          — intensity goes red-50   → red-100   → red-200   bg
// neutral:  slate        — intensity goes slate-50  → slate-100  bg (only 2 levels)
// warning:  amber        — flat (admin-only, stale_reports)

type PaletteKey =
  | 'positive-1'
  | 'positive-2'
  | 'positive-3'
  | 'negative-1'
  | 'negative-2'
  | 'negative-3'
  | 'neutral-1'
  | 'neutral-2'
  | 'warning'

const PALETTES: Record<PaletteKey, ProposalStatusColors> = {
  // ── Positive (brand green) ────────────────────────────────────────────────
  'positive-1': {
    border: 'border-brand-700/40',
    cardBg: 'bg-gradient-to-br from-brand-900/60 to-charcoal-800',
    hoverShadow: 'hover:shadow-brand-900/40',
    hoverBg: 'bg-gradient-to-br from-brand-900/20 to-brand-800/10',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-brand-500/60',
    iconBg: 'bg-brand-900/40',
    iconColor: 'text-brand-400',
    valueColor: 'text-brand-300',
    labelColor: 'text-brand-400',
    subColor: 'text-brand-500',
    divider: 'bg-brand-700/30',
    title: 'text-brand-300',
    viewLink: 'border-brand-700 text-brand-400 hover:bg-brand-900/30',
  },
  'positive-2': {
    border: 'border-brand-600/50',
    cardBg: 'bg-gradient-to-br from-brand-800/65 to-charcoal-800',
    hoverShadow: 'hover:shadow-brand-900/50',
    hoverBg: 'bg-gradient-to-br from-brand-800/25 to-brand-700/15',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-brand-400/70',
    iconBg: 'bg-brand-800/50',
    iconColor: 'text-brand-300',
    valueColor: 'text-brand-200',
    labelColor: 'text-brand-300',
    subColor: 'text-brand-400',
    divider: 'bg-brand-600/30',
    title: 'text-brand-200',
    viewLink: 'border-brand-600 text-brand-300 hover:bg-brand-800/30',
  },
  'positive-3': {
    border: 'border-brand-500/60',
    cardBg: 'bg-gradient-to-br from-brand-800/70 to-charcoal-800',
    hoverShadow: 'hover:shadow-brand-800/50',
    hoverBg: 'bg-gradient-to-br from-brand-700/30 to-brand-600/20',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-brand-400/80',
    iconBg: 'bg-brand-700/50',
    iconColor: 'text-brand-300',
    valueColor: 'text-brand-200',
    labelColor: 'text-brand-300',
    subColor: 'text-brand-400',
    divider: 'bg-brand-500/30',
    title: 'text-brand-200',
    viewLink: 'border-brand-500 text-brand-300 hover:bg-brand-700/30',
  },

  // ── Negative (red) ────────────────────────────────────────────────────────
  'negative-1': {
    border: 'border-red-700/40',
    cardBg: 'bg-gradient-to-br from-red-900/60 to-charcoal-800',
    hoverShadow: 'hover:shadow-red-900/40',
    hoverBg: 'bg-gradient-to-br from-red-900/20 to-red-800/10',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-red-500/60',
    iconBg: 'bg-red-900/40',
    iconColor: 'text-red-400',
    valueColor: 'text-red-300',
    labelColor: 'text-red-400',
    subColor: 'text-red-500',
    divider: 'bg-red-700/30',
    title: 'text-red-300',
    viewLink: 'border-red-700 text-red-400 hover:bg-red-900/30',
  },
  'negative-2': {
    border: 'border-red-600/50',
    cardBg: 'bg-gradient-to-br from-red-900/65 to-charcoal-800',
    hoverShadow: 'hover:shadow-red-900/50',
    hoverBg: 'bg-gradient-to-br from-red-800/25 to-red-700/15',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-red-400/70',
    iconBg: 'bg-red-800/50',
    iconColor: 'text-red-300',
    valueColor: 'text-red-200',
    labelColor: 'text-red-300',
    subColor: 'text-red-400',
    divider: 'bg-red-600/30',
    title: 'text-red-200',
    viewLink: 'border-red-600 text-red-300 hover:bg-red-800/30',
  },
  'negative-3': {
    border: 'border-red-500/60',
    cardBg: 'bg-gradient-to-br from-red-900/70 to-charcoal-800',
    hoverShadow: 'hover:shadow-red-800/50',
    hoverBg: 'bg-gradient-to-br from-red-700/30 to-red-600/20',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-red-400/80',
    iconBg: 'bg-red-700/50',
    iconColor: 'text-red-300',
    valueColor: 'text-red-200',
    labelColor: 'text-red-300',
    subColor: 'text-red-400',
    divider: 'bg-red-500/30',
    title: 'text-red-200',
    viewLink: 'border-red-500 text-red-300 hover:bg-red-700/30',
  },

  // ── Neutral (slate) ───────────────────────────────────────────────────────
  'neutral-1': {
    border: 'border-charcoal-600/60',
    cardBg: 'bg-gradient-to-br from-charcoal-600/70 to-charcoal-800',
    hoverShadow: 'hover:shadow-charcoal-900/40',
    hoverBg: 'bg-gradient-to-br from-charcoal-700/30 to-charcoal-600/20',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-charcoal-500/70',
    iconBg: 'bg-charcoal-700/80',
    iconColor: 'text-charcoal-300',
    valueColor: 'text-charcoal-200',
    labelColor: 'text-charcoal-300',
    subColor: 'text-charcoal-400',
    divider: 'bg-charcoal-600/40',
    title: 'text-charcoal-200',
    viewLink: 'border-charcoal-600 text-charcoal-300 hover:bg-charcoal-700/50',
  },
  'neutral-2': {
    border: 'border-charcoal-500/60',
    cardBg: 'bg-gradient-to-br from-charcoal-600/80 to-charcoal-800',
    hoverShadow: 'hover:shadow-charcoal-900/50',
    hoverBg: 'bg-gradient-to-br from-charcoal-600/30 to-charcoal-500/20',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-charcoal-400/70',
    iconBg: 'bg-charcoal-600/80',
    iconColor: 'text-charcoal-200',
    valueColor: 'text-white',
    labelColor: 'text-charcoal-200',
    subColor: 'text-charcoal-400',
    divider: 'bg-charcoal-500/40',
    title: 'text-white',
    viewLink: 'border-charcoal-500 text-charcoal-200 hover:bg-charcoal-600/50',
  },

  // ── Warning (amber) — stale reports only ─────────────────────────────────
  warning: {
    border: 'border-amber-700/40',
    cardBg: 'bg-gradient-to-br from-amber-900/60 to-charcoal-800',
    hoverShadow: 'hover:shadow-amber-900/40',
    hoverBg: 'bg-gradient-to-br from-amber-900/20 to-amber-800/10',
    barTrack: 'bg-charcoal-700',
    barFill: 'bg-amber-500/60',
    iconBg: 'bg-amber-900/40',
    iconColor: 'text-amber-400',
    valueColor: 'text-amber-300',
    labelColor: 'text-amber-400',
    subColor: 'text-amber-500',
    divider: 'bg-amber-700/30',
    title: 'text-amber-300',
    viewLink: 'border-amber-700 text-amber-400 hover:bg-amber-900/30',
  },
}

// ─── Status config registry ───────────────────────────────────────────────────

export const PROPOSAL_STATUS_CONFIG: Record<ProposalStatusKey, ProposalStatusConfig> = {
  not_started: {
    key: 'not_started',
    category: 'neutral',
    intensity: 1,
    label: 'Not Started',
    sublabel: 'No report yet',
    description: 'Surveys without any report or proposal sent yet.',
    icon: Minus,
    colors: PALETTES['neutral-1'],
    actor: 'vendor',
  },
  started_by_vendor: {
    key: 'started_by_vendor',
    category: 'positive',
    intensity: 1,
    label: 'Started',
    sublabel: 'Vendor in progress',
    description: 'Reports with content in progress but no proposal sent to the client yet.',
    icon: PenLine,
    colors: PALETTES['positive-1'],
    actor: 'vendor',
  },
  active_not_accessed: {
    key: 'active_not_accessed',
    category: 'neutral',
    intensity: 2,
    label: 'Pending',
    sublabel: 'Awaiting access',
    description: 'Proposals sent to the client but not yet opened.',
    icon: Clock,
    colors: PALETTES['neutral-2'],
    actor: 'customer',
  },
  active_awaiting_response: {
    key: 'active_awaiting_response',
    category: 'positive',
    intensity: 2,
    label: 'In Progress',
    sublabel: 'Client reviewing',
    description: 'Proposals opened by the client, awaiting acceptance or rejection.',
    icon: Eye,
    colors: PALETTES['positive-2'],
    actor: 'customer',
  },
  completed_accepted: {
    key: 'completed_accepted',
    category: 'positive',
    intensity: 3,
    label: 'Accepted',
    sublabel: 'Client approved',
    description: 'Proposals approved by the client.',
    icon: CheckCircle,
    colors: PALETTES['positive-3'],
  },
  completed_rejected: {
    key: 'completed_rejected',
    category: 'negative',
    intensity: 3,
    label: 'Rejected',
    sublabel: 'Client declined',
    description: 'Proposals declined by the client.',
    icon: ThumbsDown,
    colors: PALETTES['negative-3'],
  },
  completed_expired: {
    key: 'completed_expired',
    category: 'negative',
    intensity: 2,
    label: 'Expired',
    sublabel: 'Time elapsed',
    description: 'Proposals that passed their expiry date without a response.',
    icon: AlertCircle,
    colors: PALETTES['negative-2'],
  },
  cancelled: {
    key: 'cancelled',
    category: 'negative',
    intensity: 1,
    label: 'Cancelled',
    sublabel: 'Voided',
    description: 'Proposals that were voided before completion.',
    icon: XCircle,
    colors: PALETTES['negative-1'],
  },
  stale_reports: {
    key: 'stale_reports',
    category: 'neutral',
    intensity: 1,
    label: 'Stale Reports',
    sublabel: 'Orphaned',
    description:
      'These reports belong to surveys that no longer exist in Site Compass. They can be safely deleted.',
    icon: AlertTriangle,
    colors: PALETTES['warning'],
  },
}

/**
 * Look up status config by key. Falls back to `not_started` for unknown keys.
 */
export function getProposalStatusConfig(key: string): ProposalStatusConfig {
  return PROPOSAL_STATUS_CONFIG[key as ProposalStatusKey] ?? PROPOSAL_STATUS_CONFIG.not_started
}
